import {ReelsModule, ReelsSpinningState} from '@gongxigames/framework/slot';
import {WolfHavenSpin} from 'com/gongxigames/math/wolfhaven/engine/WolfHavenSpin';
import {GameReelsView} from 'src/gameReels/GameReelsView';
import {Bonus} from 'com/gongxigames/math/core/game/engine/bonus/Bonus';
import {PremonitionConstants} from 'src/premonition/PremonitionConstants';
import {PremonitionModule} from 'src/premonition/PremonitionModule';
import {GameLogic} from 'src/server/GameLogic';

export class GameReelsSpinningState extends ReelsSpinningState {
    public static SHOULD_PLAY_PREMONITION = Symbol('__SHOULD_PLAY_PREMONITION__');
    protected chances: any = PremonitionConstants.PREMONITION_CHANCES;
    protected shouldPlayPremonition = false;

    protected setupEvents(): void {
        super.setupEvents();
        this.setEventHandler(PremonitionModule.PREMONITION_FINISHED, this.onPremonitionFinished.bind(this));
    }

    protected onSpinResponseReceived(): void {
        this.getOutStopPositionsFromResponse();
        this.getReelsWithScatterFromResponse();
        this.view.symbolsWithHitAnimationOnReels = this.getSymbolsFromResponse(this.view.symbolsWithHitAnimation);
        this.view.oversizeSymbolsOnReels = this.getSymbolsFromResponse(this.view.oversizeSymbols);
        this.updateSpineSet();
        this.checkForFeatures();
    }

    protected checkForFeatures(): void {
        this.shouldPlayPremonition = this.module.resourceManager.isResourcesLoaded && this.isPlayPremonition();

        if (this.shouldPlayPremonition) {
            this.playFeature(GameReelsSpinningState.SHOULD_PLAY_PREMONITION);
        } else {
            this.checkExpectingResponse();
        }
    }

    protected checkExpectingResponse(): void {
        if (this._expectingResponse) {
            this._expectingResponse = false;
            this.module.gotoState(ReelsModule.STOPPING_STATE);
        }
    }

    protected playFeature(event: symbol): void {
        this.module.isReelsCanBeStopped = false;
        this.clearStoppingTimer();
        this._expectingResponse = false;
        this.module.eventManager.emit(event);
    }

    protected updateSpineSet(): void {
        const spin = this.module.logic.spinData as WolfHavenSpin;

        this.view.setDimensions(spin.getDimension());
        this.view.setNextStrip(this.module.logic.getReelSet().getReels());
    }

    protected onPremonitionFinished(): void {
        this.module.isReelsCanBeStopped = true;
        this.tryInitializeStoppingState();
    }

    protected isPlayPremonition(): boolean {
        return this.isPlayPremonitionBeforeWin() ||
            this.isPlayPremonitionBeforeFreeGames() ||
            this.isPlayPremonitionBeforePickBonus();
    }

    protected isPlayPremonitionBeforeWin(): boolean {
        return this.isEnoughWin() && Math.random() <= this.chances.win;
    }

    protected isEnoughWin(): boolean {
        const spinData = this.module.logic.spinData;
        const bet = spinData.totalBet.value();
        const win = spinData.getWin();
        const ratio = win / bet;

        return ratio >= PremonitionConstants.MIN_WIN_FOR_PREMONITION;
    }

    protected getSymbolsFromResponse(symbolTypes: number[]): number[][][] {
        const reels = this.module.logic.spinData.symbolIds;
        const foundSymbols: number[][][] = [];

        reels.forEach((reel, reelIndex) => {
            foundSymbols[reelIndex] = [];
            reel.forEach((symbol, row) => {
                if (symbolTypes.indexOf(symbol) !== -1) {
                    const isWardSymbolPosition = this.isAwardSymbolPosition(reelIndex, row);

                    if (!foundSymbols[reelIndex][symbol]) {
                        foundSymbols[reelIndex][symbol] = [];
                    }
                    !isWardSymbolPosition && foundSymbols[reelIndex][symbol].push(row);
                }
            });
        });

        return foundSymbols;
    }

    protected isAwardSymbolPosition(reelIndex: number, rowIndex: number): boolean {
        const logic = this.module.logic as GameLogic;
        const awardSymbol = logic.getRandomWildPositions();
        let isAwardPosition = false;

        if (awardSymbol.length > 0) {
            awardSymbol.forEach(pos => {
                if (pos.reel === reelIndex && pos.row === rowIndex) {
                    isAwardPosition = true;
                }
            });
        }

        return isAwardPosition;
    }

    protected isPlayPremonitionBeforeFreeGames(): boolean {
        return this.module.logic.spinData.bonusId === Bonus.FREE_GAMES_BONUS && Math.random() <= this.chances.freeGames;
    }

    protected isPlayPremonitionBeforePickBonus(): boolean {
        return this.module.logic.spinData.bonusId === Bonus.PICK_BONUS && Math.random() <= this.chances.pick;
    }

    protected get view(): GameReelsView {
        return this._module.view as GameReelsView;
    }
}
